import { Editor } from '@tiptap/react';
import classNames from 'classnames';

interface ToolbarProps {
    editor: Editor;
    onAddImage: () => void;
}

const Toolbar = ({ editor, onAddImage }: ToolbarProps) => {
    const ToolbarButton = ({ onClick, active, disabled, icon, title }: any) => (
        <div
            onClick={onClick}
            className={classNames(
                'relative p-2.5 rounded-md transition-all duration-200',
                'text-content-500/80 dark:text-content-400/80',
                'hover:bg-content-100/50 dark:hover:bg-content-800/50',
                'hover:text-content-500 dark:hover:text-content-400',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                active && 'bg-content-100 dark:bg-content-800 text-content-500 dark:text-content-400'
            )}
            title={title}
        >
            <div className={classNames(icon, 'h-4 w-4')} />
        </div>
    );

    return (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-content-100 dark:border-content-800 p-2">
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                active={editor.isActive('bold')}
                icon="icon-[ph--text-b]"
                title="Bold"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                active={editor.isActive('italic')}
                icon="icon-[ph--text-italic]"
                title="Italic"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                active={editor.isActive('strike')}
                icon="icon-[ph--text-strikethrough]"
                title="Strike"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleCode().run()}
                active={editor.isActive('code')}
                icon="icon-[ph--code]"
                title="Code"
            />

            <div className="h-4 w-px bg-content-100 dark:bg-content-800 mx-1" />

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                active={editor.isActive('heading', { level: 1 })}
                icon="icon-[ph--text-h-one]"
                title="Heading 1"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                active={editor.isActive('heading', { level: 2 })}
                icon="icon-[ph--text-h-two]"
                title="Heading 2"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                active={editor.isActive('heading', { level: 3 })}
                icon="icon-[ph--text-h-three]"
                title="Heading 3"
            />

            <div className="h-4 w-px bg-content-100 dark:bg-content-800 mx-1" />

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                active={editor.isActive('bulletList')}
                icon="icon-[ph--list-bullets]"
                title="Bullet List"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                active={editor.isActive('orderedList')}
                icon="icon-[ph--list-numbers]"
                title="Ordered List"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleTaskList().run()}
                active={editor.isActive('taskList')}
                icon="icon-[ph--check-square]"
                title="Task List"
            />

            <div className="h-4 w-px bg-content-100 dark:bg-content-800 mx-1" />

            <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                active={editor.isActive({ textAlign: 'left' })}
                icon="icon-[ph--text-align-left]"
                title="Align Left"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                active={editor.isActive({ textAlign: 'center' })}
                icon="icon-[ph--text-align-center]"
                title="Align Center"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                active={editor.isActive({ textAlign: 'right' })}
                icon="icon-[ph--text-align-right]"
                title="Align Right"
            />

            <div className="h-4 w-px bg-content-100 dark:bg-content-800 mx-1" />

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                active={editor.isActive('codeBlock')}
                icon="icon-[ph--code-block]"
                title="Code Block"
            />
            <ToolbarButton
                onClick={onAddImage}
                active={editor.isActive('image')}
                icon="icon-[ph--image]"
                title="Add Image"
            />
            <ToolbarButton
                onClick={() => {
                    const url = window.prompt('URL');
                    if (url) {
                        editor.chain().focus().setLink({ href: url }).run();
                    }
                }}
                active={editor.isActive('link')}
                icon="icon-[ph--link]"
                title="Add Link"
            />
            <ToolbarButton
                onClick={() => editor.chain().focus().insertTable().run()}
                icon="icon-[ph--table]"
                title="Insert Table"
            />

            <div className="h-4 w-px bg-content-100 dark:bg-content-800 mx-1" />

            <ToolbarButton
                onClick={() => {
                    const url = window.prompt('YouTube URL (e.g. https://www.youtube.com/watch?v=...)');
                    if (url) {
                        editor.chain().focus().setYoutubeVideo({ src: url }).run();
                    }
                }}
                icon="icon-[ph--youtube-logo]"
                title="Embed YouTube Video"
            />
        </div>
    );
};

export default Toolbar; 